import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { space, color } from '@op/opds.tokens';
import { Message } from '@messageformat/react';
import { List, ListItem, Tile } from '@op/opds.opux';
import { IconInformation } from '@op/opds.opux-icons/indicator';
import { ReactComponent as Circle } from '../../images/circle.svg';
import { ReactComponent as Rectangle } from '../../images/rectangle.svg';
import { ReactComponent as Triangle } from '../../images/triangle.svg';
import { getChartOptionsWithOverrides } from '../../util/defaultOptions';
import {
  bankAccountData,
  globalStocksData,
  investmentPortfolioData,
} from './investmentComparisonData';
import {
  getUrlEncodedCircle,
  getUrlEncodedSquare,
  getUrlEncodedTriangle,
} from '../../util/svgIcons';

const InvestmentComparisonGraph = (): JSX.Element => {
  const options = getChartOptionsWithOverrides({
    accessibility: {
      enabled: false,
    },
    title: {
      text: undefined,
    },
    chart: {
      marginTop: 24,
    },
    rangeSelector: {
      enabled: false,
    },
    scrollbar: { enabled: false },
    plotOptions: {
      series: {
        enableMouseTracking: false,
        pointStart: Date.UTC(1999, 1, 1),
        pointInterval: (365 * 24 * 3600 * 1000) / 12,
        marker: {
          enabled: false,
        },
      },
    },
    yAxis: {
      showLastLabel: true,
      showFirstLabel: true,
      opposite: false,
    },
    xAxis: {
      lineWidth: 0,
      minorGridLineWidth: 0,
      type: 'datetime',
      minorTickLength: 0,
      tickLength: 0,
      tickInterval: 365 * 24 * 3600 * 1000 * 12,
      units: [['year', [1]]],
    },

    series: [
      {
        lineColor: color.indicator.default,
        data: bankAccountData,
        marker: {
          symbol: getUrlEncodedCircle(color.indicator.default),
        },
      },
      {
        lineColor: color.indicator.notification,
        data: investmentPortfolioData,

        marker: {
          symbol: getUrlEncodedSquare(color.indicator.notification),
        },
      },
      {
        lineColor: color.text.secondary,
        data: globalStocksData,
        marker: {
          symbol: getUrlEncodedTriangle(color.text.secondary),
        },
      },
    ] as Highcharts.SeriesOptionsType[],
  });

  return (
    <>
      <Tile style={{ marginTop: space.default }}>
        <figure>
          <figcaption className="investoracademy-general-graph-title">
            <Message id="course1.investment_comparison_graph.title" />
          </figcaption>
          <div role="img" aria-labelledby="investment_comparison_graph">
            <p className="investoracademy-general-graph-description">
              <Message id="course1.investment_comparison_graph.description" />
            </p>
            <HighchartsReact highcharts={Highcharts} options={options} />
            <div style={{ display: 'none' }} id="investment_comparison_graph">
              <Message id="course1.investment_comparison_graph_text" />
            </div>
            <div style={{ textAlign: 'center' }}>
              <List
                border="none"
                size="tiny"
                indented
                style={{ display: 'inline-block', textAlign: 'left', margin: 0 }}
                aria-hidden="true"
              >
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Circle fill={color.indicator.default} aria-hidden="true" />
                    <Message id="course1.investment_comparison_graph.bank_account" />
                  </div>
                </ListItem>
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Rectangle aria-hidden="true" fill={color.indicator.notification} />
                    <Message id="course1.investment_comparison_graph.investment_portfolio" />
                  </div>
                </ListItem>
                <ListItem style={{ display: 'inline' }}>
                  <div className="investoracademy-listitem-container">
                    <Triangle aria-hidden="true" fill={color.text.secondary} />
                    <Message id="course1.investment_comparison_graph.global_stocks" />
                  </div>
                </ListItem>
              </List>
            </div>
          </div>
        </figure>
      </Tile>
      <p
        className="opds-styles-v1-text-label-small"
        style={{ display: 'flex', paddingBottom: space.default }}
      >
        <IconInformation />
        <Message id="course1.investment_comparison_graph.sources" />
      </p>
    </>
  );
};

export default InvestmentComparisonGraph;
